import { useNotify } from "ra-core";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

import { Field, SimpleSelect } from "../SimpleSelect";
import {
  fileProblem,
  MATERIAL_CATEGORIES,
  type Material,
  type MaterialCategory,
} from "./materials";
import { updateMaterial, uploadMaterial } from "./useMaterials";

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

/** Admins add one or more files to a category of the library. */
export const UploadMaterialDialog = ({
  open,
  onOpenChange,
  onSaved,
}: DialogProps) => {
  const notify = useNotify();
  const [files, setFiles] = useState<File[]>([]);
  const [category, setCategory] = useState<MaterialCategory>("videos");
  const [note, setNote] = useState("");
  const [progress, setProgress] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setFiles([]);
      setNote("");
      setProgress(null);
    }
  }, [open]);
  const problems = files
    .map((file) => ({ file, problem: fileProblem(file) }))
    .filter((item) => item.problem);

  const upload = async () => {
    let done = 0;
    try {
      for (const file of files) {
        setProgress(`Subiendo ${done + 1} de ${files.length}…`);
        await uploadMaterial(file, { category, note, sort: done });
        done++;
      }
      notify(done === 1 ? "Material subido" : `${done} archivos subidos`, {
        type: "success",
      });
      onSaved();
      onOpenChange(false);
    } catch (error) {
      notify(
        `${done ? `Se subieron ${done}. ` : ""}${
          error instanceof Error ? error.message : "No se ha podido subir"
        }`,
        { type: "error" },
      );
      if (done) onSaved();
    } finally {
      setProgress(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Subir material</DialogTitle>
          <DialogDescription>
            El nombre del archivo es el que verá el cliente. Fotos JPG/PNG (máx.
            5 MB), vídeos MP4 (máx. 16 MB) y PDF.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <Field label="Archivos">
            <Input
              type="file"
              multiple
              accept="image/jpeg,image/png,video/mp4,application/pdf"
              onChange={(event) =>
                setFiles(Array.from(event.target.files ?? []))
              }
            />
          </Field>
          {problems.map(({ file, problem }) => (
            <p key={file.name} className="text-xs text-destructive">
              {file.name}: {problem}
            </p>
          ))}
          <SimpleSelect
            label="Categoría"
            value={category}
            onChange={(value) => setCategory(value as MaterialCategory)}
            options={MATERIAL_CATEGORIES}
          />
          <Field label="Nota para el equipo (opcional)">
            <Input
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Cuándo enviarlo. El cliente no lo ve."
            />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            disabled={!files.length || problems.length > 0 || !!progress}
            onClick={upload}
          >
            {progress ?? "Subir"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Admins rename, move, annotate, reorder or hide a material. */
export const EditMaterialDialog = ({
  material,
  onOpenChange,
  onSaved,
}: Omit<DialogProps, "open"> & { material: Material | null }) => {
  const notify = useNotify();
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState<MaterialCategory>("other");
  const [note, setNote] = useState("");
  const [caption, setCaption] = useState("");
  const [sort, setSort] = useState("0");
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!material) return;
    setTitle(material.title);
    setCategory(material.category);
    setNote(material.note ?? "");
    setCaption(material.caption ?? "");
    setSort(String(material.sort));
    setActive(material.active);
  }, [material]);

  const save = async () => {
    if (!material || !title.trim()) return;
    setSaving(true);
    try {
      await updateMaterial(material.id, {
        title: title.trim(),
        category,
        note: note.trim() || null,
        caption: caption.trim() || null,
        sort: Number.parseInt(sort, 10) || 0,
        active,
      });
      notify("Guardado", { type: "success" });
      onSaved();
      onOpenChange(false);
    } catch (error) {
      notify(error instanceof Error ? error.message : "No se ha podido", {
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!material} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Editar material</DialogTitle>
          <DialogDescription>{material?.file_name}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <Field label="Nombre en el CRM">
            <Input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </Field>
          <SimpleSelect
            label="Categoría"
            value={category}
            onChange={(value) => setCategory(value as MaterialCategory)}
            options={MATERIAL_CATEGORIES}
          />
          <Field label="Nota para el equipo">
            <Input
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Cuándo enviarlo. El cliente no lo ve."
            />
          </Field>
          <Field label="Texto que acompaña al archivo (opcional)">
            <Input
              value={caption}
              onChange={(event) => setCaption(event.target.value)}
              placeholder="Lo verá el cliente debajo del archivo"
            />
          </Field>
          <Field label="Orden dentro de la categoría">
            <Input
              inputMode="numeric"
              value={sort}
              onChange={(event) => setSort(event.target.value)}
            />
          </Field>
          <label className="flex items-center justify-between gap-3 text-sm">
            Visible para los setters
            <Switch checked={active} onCheckedChange={setActive} />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button disabled={!title.trim() || saving} onClick={save}>
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
